import type z from 'zod'

import { isSome } from '../../util'

import { nouhinshoDocumentType, nouhinshoFormFieldsSchema } from './classifications/nouhinsho'
import {
  tobuKenzaiHoukokusyoFormFieldsSchema,
  tobuKenzaiHoukokusyoInspectionCompanyName,
} from './classifications/tobuKenzaiHoukokusyo'
import {
  todaEnkabutsuDocumentType,
  todaEnkabutsuFormFieldsSchema,
  todaEnkabutsuGeneralContractorName,
  todaEnkabutsuInspectionCompanyName,
} from './classifications/todaEnkabutsu'
import {
  todaFreshDocumentType,
  todaFreshFormFieldsSchema,
  todaFreshGeneralContractorName,
  todaFreshInspectionCompanyName,
} from './classifications/todaFresh'
import {
  todaHoukokusyoDocumentType,
  todaHoukokusyoDocumentType2,
  todaHoukokusyoFormFieldsSchema,
  todaHoukokusyoGeneralContractorName,
} from './classifications/todaHoukokuyso'
import {
  todaSeisekisyoDocumentType,
  todaSeisekisyoFormFieldsSchema,
  todaSeisekisyoGeneralContractorName,
  todaSeisekisyoInspectionCompanyName,
} from './classifications/todaSeisekisyo'
import type { TestResult } from './testResultSchema'

// 帳票種別 / 検査会社名 / ゼネコン名 と「その帳票が含むフォーム項目集合」の対応表。
// Modal の「基本情報」セレクトから選ばれた値で該当エントリを引き当て、
// formFieldsSchema.shape に存在する TestResult プロパティのみを描画する。
export type DocumentClassification = {
  documentType: string
  inspectionCompanyName: string | null
  generalContractorName: string | null
  // testResultSchema.pick(...) の戻り値。.shape のキーがフォームに表示すべき TestResult プロパティ集合。
  formFieldsSchema: z.ZodObject<Record<string, z.ZodTypeAny>>
}

export const documentClassifications: DocumentClassification[] = [
  {
    documentType: todaFreshDocumentType,
    inspectionCompanyName: todaFreshInspectionCompanyName,
    generalContractorName: todaFreshGeneralContractorName,
    formFieldsSchema: todaFreshFormFieldsSchema,
  },
  {
    documentType: todaSeisekisyoDocumentType,
    inspectionCompanyName: todaSeisekisyoInspectionCompanyName,
    generalContractorName: todaSeisekisyoGeneralContractorName,
    formFieldsSchema: todaSeisekisyoFormFieldsSchema,
  },
  {
    documentType: todaEnkabutsuDocumentType,
    inspectionCompanyName: todaEnkabutsuInspectionCompanyName,
    generalContractorName: todaEnkabutsuGeneralContractorName,
    formFieldsSchema: todaEnkabutsuFormFieldsSchema,
  },
  {
    documentType: todaHoukokusyoDocumentType,
    inspectionCompanyName: null,
    generalContractorName: todaHoukokusyoGeneralContractorName,
    formFieldsSchema: todaHoukokusyoFormFieldsSchema,
  },
  {
    documentType: todaHoukokusyoDocumentType2,
    inspectionCompanyName: null,
    generalContractorName: todaHoukokusyoGeneralContractorName,
    formFieldsSchema: todaHoukokusyoFormFieldsSchema,
  },
  {
    // 東部建材の圧縮強度帳票は「圧縮強度試験報告書」→「圧縮強度試験成績書」へ種別統合済み。
    // 分類上も統合先の種別で登録し、検査会社名（東部建材共同試験所）で日東の成績書と区別する。
    documentType: todaSeisekisyoDocumentType,
    inspectionCompanyName: tobuKenzaiHoukokusyoInspectionCompanyName,
    generalContractorName: null,
    formFieldsSchema: tobuKenzaiHoukokusyoFormFieldsSchema,
  },
  {
    // 納入書はプラントごとに様式が異なり検査会社・ゼネコンも固定されないため、分類軸は帳票種別のみ。
    // データ化は打設日・打設箇所（納入場所）に限定する（伝票の中身のデータ化は顧客要望に含まれない）。
    documentType: nouhinshoDocumentType,
    inspectionCompanyName: null,
    generalContractorName: null,
    formFieldsSchema: nouhinshoFormFieldsSchema,
  },
]

// セレクトの選択肢を生成するためのユニークリスト。
function uniqueNonNull(values: ReadonlyArray<string | null>): string[] {
  return Array.from(new Set(values.filter((v): v is string => v !== null)))
}

export const documentTypeOptions: string[] = uniqueNonNull(documentClassifications.map((c) => c.documentType))

export const inspectionCompanyNameOptions: string[] = uniqueNonNull(
  documentClassifications.map((c) => c.inspectionCompanyName),
)

export const generalContractorNameOptions: string[] = uniqueNonNull(
  documentClassifications.map((c) => c.generalContractorName),
)

// 帳票種別 / 検査会社名 / ゼネコン名 が features/document 配下の分類定義（documentClassifications）に
// 登録済みかどうかを判定する。サンプル一覧で「まだスキーマ未登録の値」を強調表示するために使う。
// null/undefined や空文字は「未入力」であり「未登録」ではないため false ではなく true（=強調しない）を返す。
function isRegisteredValue(value: string | null | undefined, options: readonly string[]): boolean {
  if (value === null || value === undefined || value === '') {
    return true
  }
  return options.includes(value)
}

export function isRegisteredDocumentType(value: string | null | undefined): boolean {
  return isRegisteredValue(value, documentTypeOptions)
}

export function isRegisteredInspectionCompanyName(value: string | null | undefined): boolean {
  return isRegisteredValue(value, inspectionCompanyNameOptions)
}

export function isRegisteredGeneralContractorName(value: string | null | undefined): boolean {
  return isRegisteredValue(value, generalContractorNameOptions)
}

// 1軸ぶんの (エントリ側の値, 選択値) の組。互換判定・具体度計算で共通に使う。
type ClassificationAxis = {
  entryValue: string | null
  argValue: string | null | undefined
}

// 両側が値を持ち、かつ食い違っている軸が1つでもあれば非互換。
// エントリ側 null は「この分類ではその軸を問わない」（例: 納入書はプラントごとに検査会社が
// 異なるため検査会社名を分類軸にしない）、引数側 null/undefined は「未選択なので絞り込まない」
// を意味し、どちらも不一致理由にはしない。
function isCompatible(axes: readonly ClassificationAxis[]): boolean {
  return axes.every(
    ({ entryValue, argValue }) => !(isSome(entryValue) && isSome(argValue) && entryValue !== argValue),
  )
}

// 具体度 = 両側が値を持ち厳密一致した軸の数。互換なエントリが複数あるとき、
// 軸を問わないエントリ（納入書等）より、選択値と厳密一致したエントリを優先するために使う
// （例: 検査会社名だけ選択されたとき、検査会社名 null の分類ではなく該当検査会社の分類を引き当てる）。
function specificity(axes: readonly ClassificationAxis[]): number {
  return axes.filter(({ entryValue, argValue }) => isSome(entryValue) && isSome(argValue) && entryValue === argValue)
    .length
}

/**
 * (帳票種別, 検査会社名, ゼネコン名) の組み合わせに合致する分類エントリを返す。
 * 各引数は null/undefined を許容し、null/undefined の場合はその軸では絞り込まない。
 * エントリ側が null の軸は「その分類では問わない」扱いとし、引数に値が入っていても不一致にしない
 * （納入書のように分類軸が帳票種別のみの帳票を、検査会社名等が入っていても引き当てるため）。
 * ただし引数に1つでも値があるときは、厳密一致した軸が1つもないエントリには引き当てない
 * （帳票種別が未判別のまま未登録の検査会社名等だけが入った帳票が、無関係な分類に
 * 引き当たってフォームが不当に絞られるのを防ぎ、全フィールド表示にフォールバックさせる）。
 * 互換なエントリが複数ある場合は厳密一致した軸数が最多のものを、同数なら定義順で先のものを返す。
 */
export function findDocumentClassification(args: {
  documentType: string | null | undefined
  inspectionCompanyName: string | null | undefined
  generalContractorName: string | null | undefined
}): DocumentClassification | undefined {
  const hasSelection = [args.documentType, args.inspectionCompanyName, args.generalContractorName].some(isSome)
  const candidates = documentClassifications
    .map((c) => {
      const axes: readonly ClassificationAxis[] = [
        { entryValue: c.documentType, argValue: args.documentType },
        { entryValue: c.inspectionCompanyName, argValue: args.inspectionCompanyName },
        { entryValue: c.generalContractorName, argValue: args.generalContractorName },
      ]
      return { classification: c, axes }
    })
    .filter(({ axes }) => isCompatible(axes) && (!hasSelection || specificity(axes) >= 1))
  const best = candidates.reduce<(typeof candidates)[number] | undefined>(
    (acc, x) => (acc === undefined || specificity(x.axes) > specificity(acc.axes) ? x : acc),
    undefined,
  )
  return best?.classification
}

/**
 * 選択された分類に含まれる TestResult フィールド集合を返す。
 * 一致するエントリが無い場合は null（呼び出し側は「全フィールド表示」にフォールバックする想定）。
 */
export function findFormFieldKeys(args: {
  documentType: string | null | undefined
  inspectionCompanyName: string | null | undefined
  generalContractorName: string | null | undefined
}): Set<keyof TestResult> | null {
  const matched = findDocumentClassification(args)
  if (matched === undefined) {
    return null
  }
  return new Set(Object.keys(matched.formFieldsSchema.shape) as Array<keyof TestResult>)
}
